import { useCallback, useEffect, useRef } from "react";
import { clamp } from "../utils/mathUtils";
import { isMobile } from "../utils/isMobile";
import { useLenis } from "lenis/react";
import { useCanvas } from "../utils/useCanvas";
import type Lenis from "lenis";

const defaultOptions = {
  frames: {
    desktop: { count: 666, skip: 1, directory: "/dentist/desktop" },
    mobile: { count: 333, skip: 2, directory: "/dentist/desktop" },
  },
};

export type Options = typeof defaultOptions;

export function Dentist2D({ options = defaultOptions }: { options?: Options }) {
  const isMobileDevice = isMobile();
  const { directory, count, skip } = isMobileDevice
    ? options.frames.mobile
    : options.frames.desktop;
  const lenisRef = useRef<Lenis | null>(null);
  const drawRef = useRef<
    (scroll: number, delta: number, doBlend?: boolean) => void
  >(() => {});

  const [ctx, canvas, setCanvas] = useCanvas({
    autoResize: true,
    contextId: "2d",
    resize: () => {
      drawRef.current(lenisRef.current?.actualScroll ?? 0, 0, false);
    },
  });
  const imagesRef = useRef<HTMLImageElement[]>([]);

  useEffect(() => {
    const loadImages = async () => {
      const imagePromises: Promise<HTMLImageElement>[] = [];
      for (let i = 1; i <= count; i++) {
        const frameNumber = (i * skip).toString().padStart(3, "0");
        const url = `${directory}/frame_${frameNumber}.webp`;
        imagePromises.push(
          new Promise((resolve) => {
            const img = new Image();
            img.src = url;
            img.onload = () => resolve(img);
          }),
        );
      }
      const loadedImages = await Promise.all(imagePromises);
      imagesRef.current = loadedImages;
      document.body.classList.remove("loading");
      drawRef.current(lenisRef.current?.actualScroll ?? 0, 0, false);
    };

    loadImages();
  }, [directory, count, skip]);

  const timeoutRef = useRef<number | null>(null);
  const lastDelta = useRef<number>(0);

  const draw = useCallback(
    (scroll: number, delta: number, doBlend = true) => {
      if (!canvas || !ctx || imagesRef.current.length === 0) return;

      if (delta !== 0) {
        lastDelta.current = delta;
      }

      const at = clamp(
        scroll / (document.body.scrollHeight - window.innerHeight),
        0,
        1,
      );

      const frameCount = imagesRef.current.length;
      const frameIndex = Math.floor(at * frameCount);
      const prevFrameIndex = clamp(frameIndex - 1, 0, frameCount - 1);
      const blend = (at * frameCount) % 1;
      const prevImage = imagesRef.current[prevFrameIndex]!;
      const image = imagesRef.current[clamp(frameIndex, 0, frameCount - 1)]!;

      // Clear the canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Calculate aspect ratio, scale, and offset for centering horizontally and positioning at the bottom
      const canvasAspect = canvas.width / canvas.height;
      const imageAspect = image.width / image.height;
      let drawWidth = canvas.width;
      let drawHeight = canvas.height;
      let offsetX = 0;
      let offsetY = 0;

      if (canvasAspect > imageAspect) {
        drawWidth = canvas.height * imageAspect;
        offsetX = (canvas.width - drawWidth) / 2;
      } else {
        drawHeight = canvas.width / imageAspect;
        offsetY = canvas.height - drawHeight; // Position at the bottom
      }

      document.body.style.setProperty("--draw-width", `${drawWidth / devicePixelRatio}px`);
      document.body.style.setProperty("--draw-height", `${drawHeight / devicePixelRatio}px`);

      if (doBlend) {
        ctx.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);
        if (blend > 0 && prevImage) {
          ctx.globalAlpha = 1 - blend;
          ctx.drawImage(prevImage, offsetX, offsetY, drawWidth, drawHeight);
          ctx.globalAlpha = 1;
        }
      } else {
        if (lastDelta.current >= 0) {
          ctx.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);
        } else {
          ctx.drawImage(prevImage, offsetX, offsetY, drawWidth, drawHeight);
        }
      }

      // redraw without blend after a short delay
      if (doBlend) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = window.setTimeout(() => {
          draw(scroll, delta, false);
        }, 100);
      }
    },
    [canvas, ctx],
  );

  useEffect(() => {
    drawRef.current = draw;
  }, [draw]);

  useLenis(
    (lenis) => {
      lenisRef.current = lenis;
      draw(lenis.actualScroll, lenis.lastVelocity);
    },
    [scrollY],
  );

  return (
    <canvas
      ref={setCanvas}
      style={{
        width: "100%",
        height: "100%",
        position: "absolute",
        top: 0,
        left: 0,
        zIndex: -1,
      }}
    />
  );
}
